import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser, user } = useAuth();
  const [status, setStatus] = useState("loading"); // loading, success, error, pending
  const [message, setMessage] = useState("");
  const [planName, setPlanName] = useState("");
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 10;
  const pollInterval = 2000;

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      setMessage("No se encontró la sesión de pago");
      return;
    }

    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= maxAttempts) {
      setStatus("pending");
      setMessage("El pago está siendo procesado. Te notificaremos cuando se complete.");
      return;
    }

    try {
      const response = await axios.get(`${API}/subscriptions/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === "paid") {
        setStatus("success");
        setMessage(data.message || "¡Tu suscripción ha sido activada!");
        setPlanName(data.plan_id);
        await refreshUser();
        toast.success("¡Suscripción activada!");
        return;
      } else if (data.status === "expired") {
        setStatus("error");
        setMessage("La sesión de pago ha expirado. Por favor intenta de nuevo.");
        return;
      }

      // Still pending, continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      setAttempts(prev => prev + 1);
      
      if (attempts >= maxAttempts - 1) {
        setStatus("error");
        setMessage("Error al verificar el estado del pago");
      } else {
        setTimeout(pollPaymentStatus, pollInterval);
      }
    }
  };

  const getPlanDisplayName = (planId) => {
    switch (planId) {
      case "basic":
        return "Básico";
      case "pro":
        return "Pro";
      default:
        return planId;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-orange-50/30 to-stone-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center"
      >
        {status === "loading" && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-orange-100 flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-orange-500 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-stone-900 mb-2">
              Verificando pago...
            </h1>
            <p className="text-stone-500">
              Por favor espera mientras confirmamos tu pago.
            </p>
            <div className="mt-4 text-sm text-stone-400">
              Intento {attempts + 1} de {maxAttempts}
            </div>
          </>
        )}

        {status === "success" && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", delay: 0.1 }}
              className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center"
            >
              <CheckCircle2 className="w-10 h-10 text-green-500" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h1 className="text-2xl font-bold text-stone-900 mb-2">
                ¡Pago exitoso!
              </h1>
              <p className="text-stone-600 mb-6">
                {message}
              </p>
              {planName && (
                <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl p-4 mb-6">
                  <div className="flex items-center justify-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    <span className="font-semibold">Plan {getPlanDisplayName(planName)}</span>
                  </div>
                  <p className="text-sm text-orange-100 mt-1">Ahora activo en tu cuenta</p>
                </div>
              )}
              <Link to="/">
                <Button className="w-full bg-stone-900 hover:bg-stone-800">
                  Ir al Dashboard
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </motion.div>
          </>
        )}

        {status === "error" && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center"
            >
              <XCircle className="w-10 h-10 text-red-500" />
            </motion.div>
            <h1 className="text-2xl font-bold text-stone-900 mb-2">
              Error en el pago
            </h1>
            <p className="text-stone-600 mb-6">
              {message}
            </p>
            <div className="space-y-3">
              <Link to="/pricing">
                <Button className="w-full">
                  Intentar de nuevo
                </Button>
              </Link>
              <Link to="/">
                <Button variant="outline" className="w-full">
                  Volver al Dashboard
                </Button>
              </Link>
            </div>
          </>
        )}

        {status === "pending" && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-yellow-100 flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-yellow-500 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-stone-900 mb-2">
              Pago en proceso
            </h1>
            <p className="text-stone-600 mb-6">
              {message}
            </p>
            <Link to="/">
              <Button variant="outline" className="w-full">
                Volver al Dashboard
              </Button>
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default SubscriptionSuccess;
